// Generic Map Constants that can be used across different map providers

import { BadgeStatusType } from "../common/Constants";

// Use the precise Web Mercator latitude limit:
export const MAX_LAT = 85.05112878;
export const WORLD_BOUNDS: [[number, number], [number, number]] = [
  [-MAX_LAT, -180], // South-West
  [MAX_LAT, 180], // North-East
];

// Azure Map Constants - these can be used across different components that interact with Azure Maps
export const AZURE_MAP_API_VERSION = "2.1";
export type mapTileType = "BASE" | "WEATHER";

//Define the base URL and the endpoints
const baseURL = process.env.REACT_APP_API_BASE_URL;
const mapTileEndPoint = process.env.REACT_APP_API_AZURE_MAP_TILES;
const mapSasEndPoint = process.env.REACT_APP_API_AZURE_MAP_SAS;

export const mapApiUrl = `${baseURL}${mapTileEndPoint}`;
export const mapSasApiUrl = `${baseURL}${mapSasEndPoint}`;

// How long (ms) before the sig's `expires` timestamp we should proactively fetch a new one.
export const SAS_REFRESH_BUFFER_MS = 60 * 1000;

// How long (ms) to wait before retrying after a failed sig fetch. The tile
// layer only mounts once a sig exists, so a failed fetch can't rely on tile
// load errors to trigger a retry - it must retry itself.
export const SAS_RETRY_INTERVAL_MS = 30 * 1000;

// If tiles keep failing even though the sig fetch itself keeps succeeding
// (e.g. the sig is applied incorrectly, or something other than an expired
// sig is wrong), refreshing the sig endlessly won't help. After this many
// tile-triggered refreshes within AUTH_FAILURE_WINDOW_MS, stop calling the
// sig endpoint and signal the tile layers should be remounted instead.
export const MAX_CONSECUTIVE_AUTH_FAILURES = 3;
export const AUTH_FAILURE_WINDOW_MS = 60 * 1000;

// Shape of the `/flights/map/sas` response used to authorize tile requests
// without needing a JWT/preflight on every single tile.
export interface IMapSas {
  expires: string;
  sig: string;
}

export const BASE_MAP = {
  tileSetID: "microsoft.base.road",
  errorMessage: "Base map failed to load.",
  retryMessage: "Please try again later.",
};

export const WEATHER_MAP = {
  tileSetID: "microsoft.weather.radar.main",
  maxNativeZoom: 15,
  errorMessage: "Weather map failed to load.",
  retryMessage: "Retrying in 5 min.",
};

export const MAP_ERROR_CONFIG = {
  BASE: {
    errorMessage: "Base map failed to load.",
    retryMessage: "Please try again later.",
  },
  WEATHER: {
    errorMessage: "Weather map failed to load.",
    retryMessage: "Retrying in 5 min.",
  },
  RESOURCES: {
    errorMessage: "Map resources failed to load.",
    retryMessage: "",
  },
  SAS: {
    errorMessage: "Map authorization failed to load.",
    retryMessage: "Retrying...",
  },
  NO_FLIGHTS: {
    errorMessage: "No flights found.",
    retryMessage: "",
  },
} as const;

export type MapErrorId = keyof typeof MAP_ERROR_CONFIG;

export const DEFAULT_MAP_ERROR = {
  errorMessage: "An unexpected map error occurred.",
  retryMessage: "",
};

export interface StandDisplayStatus {
  label: string;
  type: BadgeStatusType;
  color: string;
}

export interface IRunwayCondition {
  id: string;
  value: string;
  description: string;
  status?: any;
}

export interface IRunwayInfo {
  validFrom: string;
  validTo: string;
  segments: IRunwayCondition[];
}

export interface IRSCInfo {
  current: IRunwayInfo;
  prior: IRunwayInfo;
}

export type MAP_RESOURCE_STATUS = "OPERATIONAL" | "NOT OPERATIONAL" | "UNKNOWN";
export type MAP_RESOURCE_OCCUPANCY_STATUS =
  | "OCCUPIED"
  | "UNOCCUPIED"
  | "UNKNOWN";
